import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTEUDO_CHAVES } from '@/api/adminApi'
import {
  getNotificacoesCliente,
  getNotificacoesNaoLidasCount,
  marcarNotificacaoComoLida,
  marcarTodasNotificacoesLidas,
  type NotificacaoResumo,
} from '@/api/usuariosApi'
import { NotificationCard } from '@/components/NotificationCard'
import { GuestClientPanel } from '@/components/GuestClientPanel'
import { ClientPageLayout, clientPageStyles as layout } from '@/components/ClientPageLayout'
import { useUser } from '@/contexts/UserContext'
import { isGuestClient } from '@/utils/guestAuth'
import { useToast } from '@/contexts/ToastContext'
import { useRequireConteudoFlag } from '@/utils/featureFlags'
import { isNotificacaoLida } from '@/utils/notificacaoDisplay'
import { getHistoricoAgendamentoPath } from '@/utils/clienteNavigation'
import { useAgendamentoFlags } from '@/hooks/useAgendamentoFlags'
import styles from './NotificacoesPage.module.css'

type Filtro = 'todas' | 'nao_lidas'

export function NotificacoesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { loading: featureLoading } = useRequireConteudoFlag(CONTEUDO_CHAVES.notificacoes_enabled)
  const { flags: agFlags } = useAgendamentoFlags()
  const { user } = useUser()
  const [list, setList] = useState<NotificacaoResumo[]>([])
  const [unread, setUnread] = useState(0)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>('todas')
  const [marcandoTodas, setMarcandoTodas] = useState(false)

  const load = useCallback(async () => {
    if (!user?.id) return
    setErro(null)
    try {
      const [items, count] = await Promise.all([
        getNotificacoesCliente(user.id, 50),
        getNotificacoesNaoLidasCount(user.id),
      ])
      setList(items)
      setUnread(count)
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao carregar notificações'
      setErro(msg)
      toast(msg, 'error')
    }
  }, [user?.id, toast])

  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }
    let cancelled = false
    load()
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [user?.id, load])

  const visiveis = useMemo(() => {
    if (filtro === 'nao_lidas') return list.filter((n) => !isNotificacaoLida(n))
    return list
  }, [list, filtro])

  const handleMarcarLida = async (n: NotificacaoResumo) => {
    if (!user?.id || isNotificacaoLida(n)) return
    try {
      await marcarNotificacaoComoLida(n.id, user.id)
      setList((prev) => prev.map((item) => (item.id === n.id ? { ...item, lida: 1 } : item)))
      setUnread((c) => Math.max(0, c - 1))
    } catch {
      toast('Não foi possível marcar como lida.', 'error')
    }
  }

  const handleClick = async (n: NotificacaoResumo) => {
    await handleMarcarLida(n)
    if (n.agendamento_id && agFlags.historico) {
      navigate(getHistoricoAgendamentoPath(n.agendamento_id))
    }
  }

  const handleMarcarTodas = async () => {
    if (!user?.id || unread === 0) return
    setMarcandoTodas(true)
    try {
      await marcarTodasNotificacoesLidas(user.id)
      setList((prev) => prev.map((n) => ({ ...n, lida: 1 })))
      setUnread(0)
      toast('Todas as notificações foram marcadas como lidas.', 'success')
    } catch {
      toast('Erro ao marcar notificações.', 'error')
    } finally {
      setMarcandoTodas(false)
    }
  }

  if (featureLoading) return null

  if (isGuestClient(user)) {
    return (
      <ClientPageLayout>
        <GuestClientPanel variant="notificacoes" showAgendar={false} />
      </ClientPageLayout>
    )
  }

  return (
    <ClientPageLayout
      loading={loading}
      empty={!loading && !erro && visiveis.length === 0}
      emptyMessage={
        filtro === 'nao_lidas'
          ? 'Não tem notificações por ler.'
          : 'Quando houver lembretes ou avisos, aparecem aqui.'
      }
      emptyIcon="🔔"
    >
      <div className={styles.headerRow}>
        <div>
          <p className={layout.lead}>
            Lembretes de agendamento e avisos da sua conta.
          </p>
          {unread > 0 && (
            <p className={styles.unreadHint}>
              {unread} não lida{unread > 1 ? 's' : ''}
            </p>
          )}
        </div>
        {unread > 0 && (
          <button
            type="button"
            className={styles.markAllBtn}
            onClick={() => handleMarcarTodas()}
            disabled={marcandoTodas}
          >
            {marcandoTodas ? 'A marcar…' : 'Marcar todas lidas'}
          </button>
        )}
      </div>

      {erro && (
        <div className={styles.erroBox} role="alert">
          {erro}
        </div>
      )}

      <div className={styles.filters} role="tablist" aria-label="Filtrar notificações">
        <button
          type="button"
          role="tab"
          aria-selected={filtro === 'todas'}
          className={`${styles.filterBtn} ${filtro === 'todas' ? styles.filterBtnActive : ''}`}
          onClick={() => setFiltro('todas')}
        >
          Todas ({list.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={filtro === 'nao_lidas'}
          className={`${styles.filterBtn} ${filtro === 'nao_lidas' ? styles.filterBtnActive : ''}`}
          onClick={() => setFiltro('nao_lidas')}
        >
          Não lidas ({unread})
        </button>
      </div>

      {!loading && !erro && visiveis.length > 0 && (
        <ul className={styles.list}>
          {visiveis.map((n) => (
            <li key={n.id}>
              <NotificationCard notificacao={n} onClick={() => handleClick(n)} />
            </li>
          ))}
        </ul>
      )}
    </ClientPageLayout>
  )
}
